import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const ContactUs = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const form = e.target;
        const formDataObj = new FormData(form);
        setIsSubmitting(true);
        setError(null);

        try {
            const response = await fetch('/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams(formDataObj).toString()
            });

            if (response.ok) {
                setSubmitted(true);
                setError(null);
                setFormData({ name: '', email: '', subject: '', message: '' });
                setTimeout(() => {
                    setSubmitted(false);
                }, 5000);
            } else {
                throw new Error('Form submission failed');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setError('There was an error submitting your form. Please try again.');
            setSubmitted(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto py-8">
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 sm:p-8 lg:p-12">
                <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-6">Contact Us</h1>

                <div className="prose prose-gray max-w-none mb-8">
                    <p className="text-gray-700 dark:text-gray-300 mb-4">
                        Have a question, feedback, or suggestion? We'd love to hear from you! Please fill out the form below
                        and we'll get back to you as soon as possible.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="flex items-start gap-4 p-6 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                        <div className="flex-shrink-0 w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center">
                            <i className="fas fa-envelope text-white"></i>
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Email</h3>
                            <p className="text-gray-600 dark:text-gray-400 text-sm"><a className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300" href="mailto:contact@rewriteking.com">contact@rewriteking.com</a></p>
                        </div>
                    </div>

                    <div className="flex items-start gap-4 p-6 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                        <div className="flex-shrink-0 w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center">
                            <i className="fas fa-clock text-white"></i>
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Response Time</h3>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">Within 24 hours</p>
                        </div>
                    </div>
                </div>

                {/* Success Message */}
                {submitted && (
                    <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border-2 border-green-200 dark:border-green-800 rounded-lg">
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-6 h-6 mt-0.5">
                                <i className="fas fa-check-circle text-green-600 dark:text-green-400 text-xl"></i>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-green-800 dark:text-green-200 font-semibold mb-1">
                                    Message Sent Successfully!
                                </h3>
                                <p className="text-green-700 dark:text-green-300 text-sm">
                                    Thank you for contacting us. We've received your message and will get back to you within 24 hours.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border-2 border-red-200 dark:border-red-800 rounded-lg">
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-6 h-6 mt-0.5">
                                <i className="fas fa-exclamation-circle text-red-600 dark:text-red-400 text-xl"></i>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-red-800 dark:text-red-200 font-semibold mb-1">
                                    Submission Failed
                                </h3>
                                <p className="text-red-700 dark:text-red-300 text-sm">
                                    {error}
                                </p>
                            </div>
                            <button
                                onClick={() => setError(null)}
                                className="flex-shrink-0 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
                                aria-label="Close error message"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                    </div>
                )}

                <form
                    name="contact"
                    method="POST"
                    data-netlify="true"
                    data-netlify-honeypot="bot-field"
                    onSubmit={handleSubmit}
                    className="space-y-6"
                    style={{ opacity: submitted ? 0.6 : 1, pointerEvents: submitted || isSubmitting ? 'none' : 'auto' }}
                >
                    <input type="hidden" name="form-name" value="contact" />
                    <input type="hidden" name="bot-field" />

                    <div>
                        <label htmlFor="name" className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                            Name *
                        </label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:border-primary-600 focus:ring-4 focus:ring-primary-100 dark:focus:ring-primary-900/50 focus:outline-none transition-all bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                            placeholder="Your name"
                        />
                    </div>

                    <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                            Email *
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:border-primary-600 focus:ring-4 focus:ring-primary-100 dark:focus:ring-primary-900/50 focus:outline-none transition-all bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                            placeholder="your.email@example.com"
                        />
                    </div>

                    <div>
                        <label htmlFor="subject" className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                            Subject *
                        </label>
                        <input
                            type="text"
                            id="subject"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:border-primary-600 focus:ring-4 focus:ring-primary-100 dark:focus:ring-primary-900/50 focus:outline-none transition-all bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                            placeholder="What can we help you with?"
                        />
                    </div>

                    <div>
                        <label htmlFor="message" className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                            Message *
                        </label>
                        <textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            required
                            rows="6"
                            className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:border-primary-600 focus:ring-4 focus:ring-primary-100 dark:focus:ring-primary-900/50 focus:outline-none transition-all resize-y bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                            placeholder="Tell us more about your inquiry..."
                        ></textarea>
                    </div>

                    <button
                        type="submit"
                        disabled={isSubmitting || submitted}
                        className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                    >
                        {isSubmitting ? (
                            <span className="flex items-center justify-center gap-2">
                                <i className="fas fa-spinner fa-spin"></i>
                                Sending...
                            </span>
                        ) : submitted ? (
                            <span className="flex items-center justify-center gap-2">
                                <i className="fas fa-check"></i>
                                Message Sent!
                            </span>
                        ) : (
                            <span className="flex items-center justify-center gap-2">
                                <i className="fas fa-paper-plane"></i>
                                Send Message
                            </span>
                        )}
                    </button>
                </form>

                <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-800">
                    <Link to="/" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium">
                        ← Back to Home
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default ContactUs;

